import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { sortUsersByRole } from '../../utils/userSort';

const AddProjectPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [allUsers, setAllUsers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const memberDropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target)) {
        setMemberDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    customer: '',
    status: 'Not Started',
    startDate: '',
    deadline: '',
    progress: 0,
    tags: '',
    description: '',
    members: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUsers();
    if (!editId) return;
    loadProject(editId);
  }, [editId]);

  const loadUsers = async () => {
    try {
      const response = await api.getUsers({ limit: 1000 });
      if (!response.success) return;
      setAllUsers(response.data || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load users');
    }
  };

  const loadProject = async (id) => {
    try {
      const response = await api.getProject(id);
      if (!response.success || !response.data) return;
      const project = response.data;
      setFormData({
        name: project.name || '',
        customer: project.customer || '',
        status: project.status || 'Not Started',
        startDate: project.startDate ? new Date(project.startDate).toISOString().slice(0, 10) : '',
        deadline: project.deadline ? new Date(project.deadline).toISOString().slice(0, 10) : '',
        progress: project.progress || 0,
        tags: (project.tags || []).join(', '),
        description: project.description || '',
        members: (project.members || []).map((member) => member._id || member.id),
      });
    } catch (error) {
      toast.error(error.message || 'Failed to load project');
    }
  };

  const onChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (formData.startDate && formData.deadline) {
      const start = new Date(formData.startDate);
      const deadline = new Date(formData.deadline);
      start.setHours(0, 0, 0, 0);
      deadline.setHours(0, 0, 0, 0);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(deadline.getTime()) && deadline < start) {
        toast.warning('Deadline cannot be before the start date');
        return;
      }
    }
    try {
      setSaving(true);
      const payload = {
        ...formData,
        progress: Number(formData.progress || 0),
      };
      if (editId) {
        await api.updateProject(editId, payload);
        toast.success('Project updated');
      } else {
        await api.createProject(payload);
        toast.success('Project created');
      }
      navigate('/admin/projects');
    } catch (error) {
      toast.error(error.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const toggleMember = (memberId) => {
    setFormData((prev) => {
      const exists = prev.members.includes(memberId);
      return {
        ...prev,
        members: exists
          ? prev.members.filter((id) => id !== memberId)
          : [...prev.members, memberId],
      };
    });
  };

  // Live mirror of the selection, read by the ordering memo below without being one of its deps.
  const memberSelectionRef = useRef(formData.members);
  memberSelectionRef.current = formData.members;

  const filteredUsers = useMemo(() => {
    const sorted = sortUsersByRole(
      allUsers.filter((user) => {
        const q = memberSearch.trim().toLowerCase();
        if (!q) return true;
        return `${user.name || ''} ${user.email || ''} ${user.role || ''}`.toLowerCase().includes(q);
      })
    );
    // Selected members float to the top so the current selection is always visible first. This order is
    // snapshotted when the dropdown opens or the search changes — NOT on every toggle — otherwise
    // ticking a checkbox reshuffles the rows and loses the user's scroll position mid-selection.
    const selectedSet = new Set((memberSelectionRef.current || []).map((id) => String(id)));
    const selected = sorted.filter((user) => selectedSet.has(String(user._id || user.id)));
    const rest = sorted.filter((user) => !selectedSet.has(String(user._id || user.id)));
    return [...selected, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, memberSearch, memberDropdownOpen]);

  const selectedUsers = allUsers.filter((user) => formData.members.includes(user._id || user.id));

  return (
    <div style={{ padding: '16px', display: 'flex', justifyContent: 'center' }}>
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '900px',
        }}
      >
        <h3 style={{ marginTop: 0, color: '#2d3748' }}>{editId ? 'Edit Project' : 'Add New Project'}</h3>
        <form onSubmit={onSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <input required placeholder="Project name" value={formData.name} onChange={(e) => onChange('name', e.target.value)} style={inputStyle} />
            <input required placeholder="Customer" value={formData.customer} onChange={(e) => onChange('customer', e.target.value)} style={inputStyle} />
            <select value={formData.status} onChange={(e) => onChange('status', e.target.value)} style={inputStyle}>
              <option>Not Started</option>
              <option>In Progress</option>
              <option>On Hold</option>
              <option>Cancelled</option>
              <option>Finished</option>
            </select>
            <input type="number" min="0" max="100" placeholder="Progress %" value={formData.progress} onChange={(e) => onChange('progress', e.target.value)} style={inputStyle} />
            <input type="date" required value={formData.startDate} onChange={(e) => onChange('startDate', e.target.value)} style={inputStyle} />
            <input type="date" required min={formData.startDate || undefined} value={formData.deadline} onChange={(e) => onChange('deadline', e.target.value)} style={inputStyle} />
          </div>
          <input placeholder="Tags (comma separated)" value={formData.tags} onChange={(e) => onChange('tags', e.target.value)} style={{ ...inputStyle, marginTop: '12px', width: '100%' }} />

          <div ref={memberDropdownRef} style={{ marginTop: '12px', position: 'relative' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#2d3748', marginBottom: '8px' }}>
              Members (Admin + Employee)
            </div>
            <button
              type="button"
              onClick={() => setMemberDropdownOpen((prev) => !prev)}
              style={memberDropdownTriggerStyle}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedUsers.length > 0
                  ? `${selectedUsers.length} member${selectedUsers.length > 1 ? 's' : ''} selected`
                  : 'Select members'}
              </span>
              <span style={{ fontSize: '12px', color: '#718096' }}>{memberDropdownOpen ? '▲' : '▼'}</span>
            </button>

            {memberDropdownOpen && (
              <div style={memberDropdownPanelStyle}>
                <input
                  placeholder="Search members..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  style={{ ...inputStyle, width: '100%', marginBottom: '8px' }}
                />
                <div style={{ maxHeight: '220px', overflowY: 'auto', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
                  {filteredUsers.map((user) => {
                    const userId = user._id || user.id;
                    const checked = formData.members.includes(userId);
                    return (
                      <label
                        key={userId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 2px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f7fafc',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleMember(userId)}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: 600 }}>
                            {user.name || 'Unnamed User'} ({user.role || '-'})
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
              {selectedUsers.length === 0 ? (
                <span style={{ fontSize: '12px', color: '#718096' }}>No members selected</span>
              ) : selectedUsers.map((user) => (
                <span
                  key={user._id || user.id}
                  style={{
                    background: '#ebf8ff',
                    color: '#2b6cb0',
                    border: '1px solid #bee3f8',
                    borderRadius: '999px',
                    padding: '3px 10px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {user.name}
                </span>
              ))}
            </div>
          </div>

          <textarea placeholder="Description" value={formData.description} onChange={(e) => onChange('description', e.target.value)} style={{ ...inputStyle, marginTop: '12px', width: '100%', minHeight: '90px', resize: 'vertical', paddingTop: '10px' }} />
          <div style={{ marginTop: '14px', display: 'flex', gap: '8px' }}>
            <button type="submit" disabled={saving} style={primaryBtnStyle}>{saving ? 'Saving...' : 'Save Project'}</button>
            <button type="button" onClick={() => navigate('/admin/projects')} style={secondaryBtnStyle}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const inputStyle = {
  height: '38px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  padding: '0 12px',
  fontSize: '13px',
};

const primaryBtnStyle = {
  height: '36px',
  border: 'none',
  borderRadius: '6px',
  padding: '0 14px',
  cursor: 'pointer',
  color: 'white',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  fontWeight: 600,
};

const secondaryBtnStyle = {
  height: '36px',
  border: '1px solid #cbd5e0',
  borderRadius: '6px',
  padding: '0 14px',
  cursor: 'pointer',
  color: '#2d3748',
  background: '#fff',
  fontWeight: 600,
};

const memberDropdownTriggerStyle = {
  width: '100%',
  height: '38px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  padding: '0 12px',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#fff',
  color: '#2d3748',
  cursor: 'pointer',
};

const memberDropdownPanelStyle = {
  position: 'absolute',
  top: '72px',
  left: 0,
  right: 0,
  zIndex: 20,
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
  padding: '10px',
};

export default AddProjectPage;
